import { URLHelper } from '@common';
import { useCallback, useRef, useState } from 'react';
import { useEvent } from 'react-use';
import { AppError } from './AppError';
import Styles from './Styles.module.scss';
import useAppname from './useAppname';

export default function AppFrame({ accessToken, path }: IProps) {
    const { appName, maindomain } = useAppname();
    const [linkStatus, setLinkStatus] = useState<LinkStatusResult>();

    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const iframeUrl = new URLHelper(`https://${appName}-proxy.${maindomain}/iframe-auth/`, { accessToken });

    // No `credentialless` here: it loads the frame in an ephemeral, cookie-less
    // storage partition, so the app never gets its own session cookie back.
    const props = {
        className: Styles.AppFrame,
        src: iframeUrl.toString(),
    };

    const onIframeMessage = useCallback(
        (e: MessageEvent<IframeAuthMessage>) => {
            if (e.origin !== iframeUrl.origin) return;

            const { action, result } = e.data;

            if (action === 'link-check') {
                setLinkStatus(result);

                if (result.status === 'ok') {
                    iframeRef.current?.contentWindow?.postMessage({ action: 'navigate', path: path ?? '' }, iframeUrl.origin);

                    document.getElementById('app-page')?.scrollIntoView(true);
                }
            }
        },
        [iframeUrl.toString(), accessToken]
    );

    const onIframeLoad = useCallback(() => {
        try {
            const iframeWindow = iframeRef.current?.contentWindow;
            if (!iframeWindow) return;

            iframeWindow.open = function (url, target, features) {
                // Intercept and navigate iframe instead
                if (iframeRef.current) {
                    iframeRef.current.src = url as string;
                }
                return null; // Return null since no window was opened
            };
        } catch (e) {
            console.log('Cross-origin, cannot intercept');
        }
    }, []);

    useEvent('message', onIframeMessage);
    useEvent('onLoad', onIframeLoad, iframeRef.current);

    if (linkStatus?.status === 'error') {
        return <AppError />;
    }

    return (
        <iframe
            onError={e => console.error(e)}
            ref={iframeRef as any}
            title={appName || 'AppPage'}
            sandbox="allow-downloads allow-forms allow-scripts allow-same-origin allow-popups"
            {...props}
        />
    );
}

interface IProps {
    accessToken?: string;
    path?: string;
}

interface IframeAuthMessage {
    type: 'iframe-auth';
    action: 'link-check';
    result: LinkStatusResult;
}

interface LinkStatusResult {
    status: 'ok' | 'error';
    message: string;
    statusCode?: number;
    text?: string;
}
